import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard, Button, Icon, SearchInput, FilterSelect, Modal, ModalClose } from '../../campaign-os';
import { usePageChrome } from '../PageChrome';
import { useApiData } from '../data/useApiData';
import { useToast } from '../../../../components/ui/Toast';
import { templateService, getErrorMessage } from '../../../../services/apiService';
import { TemplateEditor } from './TemplateBuilder';
import { formatShortDate } from '../data/format';
import { modeOf, TEMPLATE_MODES } from '../data/templateModes';

/**
 * Templates — REAL, database-backed email templates (GET/POST/PATCH/DELETE
 * /api/v1/email-templates). A template is the content of a campaign email: subject and body,
 * written either in the scratch pad or the guided form. The New Campaign wizard picks one and the
 * backend renders it through the same engine the preview uses, so what is previewed here is
 * exactly what is sent.
 *
 * A compact list (user decision, 25 Oct 2026): one row per template; a row opens the full-page
 * editor with the live preview; "New Template" opens the dialog, which starts as a scratch pad.
 *
 * Editing a template does NOT rewrite campaigns already sent: each campaign stores its own
 * rendered copy. It DOES change what future campaigns using it will send.
 */

const CATEGORY_TONE = {
  Compliance: 'bg-cos-status-teal-bg text-cos-teal-deep',
  Verification: 'bg-cos-status-blue-bg text-cos-status-blue',
  Outreach: 'bg-cos-status-amber-bg text-cos-status-amber',
  Custom: 'bg-cos-surface-hover text-cos-ink-secondary',
};

function CategoryChip({ value }) {
  return (
    <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold ${CATEGORY_TONE[value] || CATEGORY_TONE.Custom}`}>
      {value}
    </span>
  );
}

function ModeChip({ template }) {
  const mode = TEMPLATE_MODES.find((m) => m.key === modeOf(template)) ?? TEMPLATE_MODES[1];
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-cos-line bg-white/70 px-2 py-0.5 text-[11px] font-semibold text-cos-ink-secondary" title={mode.hint}>
      <Icon name={mode.icon} className="text-[14px]" />
      {mode.label}
    </span>
  );
}

function RowAction({ icon, label, onClick, danger = false }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={[
        'grid h-8 w-8 place-items-center rounded-lg transition-colors',
        danger ? 'text-cos-status-coral hover:bg-cos-status-coral-bg' : 'text-cos-ink-secondary hover:bg-white hover:text-cos-ink',
      ].join(' ')}
    >
      <Icon name={icon} className="text-[18px]" />
    </button>
  );
}

const TH = 'whitespace-nowrap px-3 py-2.5 text-left text-2xs font-bold uppercase tracking-[0.08em] text-cos-ink-muted';

export function TemplatesPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // template object | 'new' | null
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const params = useMemo(
    () => ({ ...(category !== 'All' ? { category } : {}), ...(search.trim() ? { search: search.trim() } : {}) }),
    [category, search]
  );
  const api = useApiData(() => templateService.list(params), [params], { cacheKey: `templates:${JSON.stringify(params)}` });

  const rows = api.data?.data ?? [];
  const categories = useMemo(
    () => ['All', ...(api.data?.categories ?? ['Compliance', 'Verification', 'Outreach', 'Custom'])],
    [api.data]
  );

  const reload = api.refetch;
  const open = useCallback((t) => navigate(`/campaigning-tool/templates/${t.id}`), [navigate]);

  const handleSaved = useCallback(
    (msg) => {
      setEditing(null);
      toast.success(msg);
      reload();
    },
    [toast, reload]
  );

  const duplicate = async (t) => {
    try {
      const res = await templateService.duplicate(t.id);
      if (res.success) {
        toast.success(`“${t.name}” duplicated`);
        reload();
      }
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Could not duplicate the template');
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      const res = await templateService.remove(confirmDelete.id);
      if (res.success) {
        toast.success(`“${confirmDelete.name}” deleted`);
        setConfirmDelete(null);
        reload();
      }
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Could not delete the template');
    } finally {
      setBusy(false);
    }
  };

  usePageChrome(
    () => ({
      title: 'Templates',
      subtitle: 'Reusable email content for campaigns',
      actions: (
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon="refresh" onClick={reload}>
            Refresh
          </Button>
          <Button variant="primary" icon="add" onClick={() => setEditing('new')}>
            New Template
          </Button>
        </div>
      ),
    }),
    [reload]
  );

  return (
    <div className="flex flex-col gap-5">
      <GlassCard>
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[220px] flex-1">
            <SearchInput value={search} onChange={setSearch} placeholder="Search templates by name, subject or category…" />
          </div>
          <FilterSelect icon="category" label="Category" value={category} onChange={setCategory} options={categories} />
          <span className="ml-auto text-[13px] text-cos-ink-secondary">
            {api.loading ? 'Loading…' : `${rows.length} template${rows.length === 1 ? '' : 's'}`}
          </span>
        </div>
      </GlassCard>

      {api.error ? (
        <GlassCard>
          <p className="text-sm text-cos-status-coral">{api.error}</p>
        </GlassCard>
      ) : null}

      {!api.loading && rows.length === 0 ? (
        <GlassCard>
          <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
            <Icon name="dashboard_customize" className="text-[36px] text-cos-ink-secondary/50" />
            <p className="max-w-[340px] text-sm text-cos-ink-secondary">
              {search || category !== 'All'
                ? 'No templates match this filter.'
                : 'No templates yet. Create one and it becomes available to every campaign.'}
            </p>
            <Button variant="primary" icon="add" onClick={() => setEditing('new')}>
              New Template
            </Button>
          </div>
        </GlassCard>
      ) : null}

      {rows.length > 0 ? (
        <GlassCard className="!p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-cos-line">
                  <th className={TH}>Template</th>
                  <th className={TH}>Written as</th>
                  <th className={TH}>Category</th>
                  <th className={TH}>Subject</th>
                  <th className={`${TH} text-right`}>Uses</th>
                  <th className={TH}>Updated</th>
                  <th className="w-1 px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => open(t)}
                    className="group cursor-pointer border-b border-cos-line-subtle transition-colors last:border-0 hover:bg-[rgba(13,168,155,0.05)]"
                  >
                    <td className="px-3 py-2.5 align-middle">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-cos-status-teal-bg">
                          <Icon name="mail" className="text-[17px] text-cos-teal-deep" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-[14px] font-bold leading-tight text-cos-ink">{t.name}</p>
                          <p className="truncate text-[12px] leading-[17px] text-cos-ink-secondary">{t.description || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      <ModeChip template={t} />
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      <CategoryChip value={t.category} />
                    </td>
                    <td className="max-w-[320px] px-3 py-2.5 align-middle">
                      <p className="truncate text-[13px] text-cos-ink">{t.subject}</p>
                    </td>
                    <td className="mono px-3 py-2.5 text-right align-middle text-[13px] text-cos-ink-secondary">{t.used_count}</td>
                    <td className="mono whitespace-nowrap px-3 py-2.5 align-middle text-[12px] text-cos-ink-secondary">{formatShortDate(t.updated_at)}</td>
                    <td className="px-2 py-2.5 align-middle">
                      <div className="flex items-center justify-end gap-0.5 opacity-70 transition-opacity group-hover:opacity-100">
                        <RowAction icon="open_in_full" label="Open on a full page" onClick={() => open(t)} />
                        <RowAction icon="edit" label="Quick edit" onClick={() => setEditing(t)} />
                        <RowAction icon="content_copy" label="Duplicate" onClick={() => duplicate(t)} />
                        <RowAction icon="delete" label="Delete" danger onClick={() => setConfirmDelete(t)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      ) : null}

      {editing ? (
        <TemplateEditor template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={handleSaved} />
      ) : null}

      <Modal open={Boolean(confirmDelete)} onClose={() => !busy && setConfirmDelete(null)} size="md" labelledBy="tpl-del">
        <div className="p-6">
          <ModalClose onClose={() => !busy && setConfirmDelete(null)} />
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-cos-status-coral-bg">
              <Icon name="delete" className="text-[21px] text-cos-status-coral" />
            </span>
            <div className="min-w-0">
              <h2 id="tpl-del" className="text-base font-bold text-cos-ink">
                Delete “{confirmDelete?.name}”?
              </h2>
              <p className="mt-1 text-sm leading-[21px] text-cos-ink-secondary">
                It will no longer be available to new campaigns. Campaigns already sent with it keep their content and
                history.
              </p>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmDelete(null)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="danger" icon="delete" onClick={remove} disabled={busy}>
              {busy ? 'Deleting…' : 'Delete template'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default TemplatesPage;
